/*
 * APS Favorites (assets/aps-favorites.js)
 *
 * - The heart on a product photo saves that product as a favorite.
 * - Signed-in shoppers: saved to their customer account through the Cloudflare Worker, so their
 *   favorites follow them to every device.
 * - Signed-out shoppers: saved in this browser, then moved into their account when they sign in.
 * - Keeps the header count up to date and runs the My Favorites page (search, chips, sort, remove).
 *
 * Settings come from <script id="aps-fav-data"> (snippets/aps-fav-boot.liquid).
 * Nothing secret is in this file: the signed pass only proves which customer is signed in.
 */
(function () {
  'use strict';

  if (window.ApsFavorites) return;

  var MAX = 400;
  // Right after a save, Shopify can briefly print the old list on the next page. For 1 minute we trust
  // the list the Worker last confirmed on this device; if it and the page disagree we ask the Worker.
  var FRESH_MS = 60 * 1000;
  var KEYS = {
    guest: 'aps-fav:guest:v1', // this device's favorites while signed out: [{ id, h, t }]
    acct: 'aps-fav:acct:v1', // last list the Worker confirmed: { cid, ids, at }
    queue: 'aps-fav:queue:v1', // changes not confirmed yet: { cid, ops }
    handles: 'aps-fav:handles:v1', // product id -> handle, to load cards on the favorites page
    titles: 'aps-fav:titles:v1', // (session) upsell card title -> product
    noted: 'aps-fav:noted:v1', // (session) first-save message already shown
  };
  var HEART_SVG =
    '<svg class="aps-fav__icon" width="20" height="20" viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
    '<path d="M12 20.4l-1.2-1.1C6.1 15 3 12.2 3 8.7 3 5.9 5.2 3.7 8 3.7c1.6 0 3.1.7 4 1.9 .9-1.2 2.4-1.9 4-1.9 2.8 0 5 2.2 5 5 0 3.5-3.1 6.3-7.8 10.6L12 20.4z"/></svg>';
  var UPSELL_IMG = 'lb-upsell-product-page-addon img.ppa-prod-img';
  var UPSELL_BOX = '.ppa-prod-container, .ppa-prod-container-grid, .ppa-single-lt-cont';

  // ------------------------------------------------------------------ helpers

  function readJson(el) {
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  function makeStore(kind) {
    return {
      get: function (key, fallback) {
        try {
          var v = window[kind].getItem(key);
          return v ? JSON.parse(v) : fallback;
        } catch (e) {
          return fallback;
        }
      },
      set: function (key, value) {
        try {
          window[kind].setItem(key, JSON.stringify(value));
        } catch (e) {
          /* private mode or full: keep going without it */
        }
      },
      del: function (key) {
        try {
          window[kind].removeItem(key);
        } catch (e) {
          /* ignore */
        }
      },
    };
  }

  var store = makeStore('localStorage');
  var session = makeStore('sessionStorage');

  function fill(template, values) {
    return String(template || '').replace(/\[(\w+)\]/g, function (match, key) {
      return values[key] !== undefined ? values[key] : match;
    });
  }

  function toSet(list) {
    var s = new Set();
    list.forEach(function (x) {
      s.add(x);
    });
    return s;
  }

  function isId(v) {
    return typeof v === 'string' && /^[1-9]\d{0,19}$/.test(v);
  }

  function validToken(t) {
    return !!(t && isId(String(t.cid)) && /^\d{9,11}$/.test(String(t.ts)) && /^[0-9a-f]{64}$/.test(String(t.sig)));
  }

  function norm(text) {
    return String(text || '')
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim();
  }

  // ------------------------------------------------------------------ settings

  var dataEl = document.getElementById('aps-fav-data');
  var CFG = readJson(dataEl);
  if (dataEl && !CFG && window.console) {
    console.error('[APS Favorites] The settings in snippets/aps-fav-boot.liquid are not valid; check the lines you edited.');
  }
  CFG = CFG || {};
  var S = Object.assign(
    {
      add: 'Add to favorites',
      remove: 'Remove from favorites',
      count_one: '1 favorite',
      count_other: '[count] favorites',
      saved: 'Saved to My Favorites.',
      saved_device: 'Saved on this device. Sign in to keep your favorites on all your devices.',
      view: 'View favorites',
      sign_in: 'Sign in',
      merged_one: 'We added 1 favorite from this device to your account.',
      merged_other: 'We added [count] favorites from this device to your account.',
      full: 'Your favorites list is full ([max]). Remove a few to add more.',
      error: "Sorry, that didn't save. Please try again.",
      retrying: "Having trouble saving your favorites. We'll keep trying.",
    },
    CFG.strings || {}
  );
  var ROOT = String(CFG.root || (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/');
  if (ROOT.slice(-1) !== '/') ROOT += '/';
  var PAGE_URL = CFG.page || ROOT + 'pages/my-favorites';
  var ENDPOINT = String(CFG.endpoint || '').replace(/\/+$/, '');
  var endpointOk =
    (/^https:\/\/[a-z0-9.-]+(:\d+)?$/i.test(ENDPOINT) || /^http:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/.test(ENDPOINT)) &&
    ENDPOINT.indexOf('CHANGE-ME') === -1;
  var token = validToken(CFG.token)
    ? { cid: String(CFG.token.cid), ts: String(CFG.token.ts), sig: String(CFG.token.sig) }
    : null;
  var account = !!(CFG.signedIn && token && endpointOk);

  if (CFG.signedIn && !account && window.console) {
    console.info(
      '[APS Favorites] Saving favorites on this device only: ' +
        (!endpointOk
          ? 'the Worker address in snippets/aps-fav-boot.liquid is not filled in yet.'
          : 'the secret in snippets/aps-fav-secret.liquid is not filled in yet.')
    );
  }

  // ------------------------------------------------------------------ state

  var ids = []; // what the hearts show, oldest first
  var confirmed = []; // account: last list the Worker confirmed
  var queue = []; // account: changes waiting to be sent: { op, ids }
  var guest = []; // signed out: [{ id, h, t }]
  var handles = store.get(KEYS.handles, {}) || {};
  var page = null;
  var inFlight = false;
  var retryTimer = null;
  var failures = 0;
  var warnedRetry = false;
  var needsCheck = false; // the page's list and this device's last confirmed list disagree
  var changes = 0; // goes up whenever the list changes here, so an older answer can't overwrite a newer one

  function normalizeGuest(list) {
    var seen = new Set();
    return (Array.isArray(list) ? list : [])
      .filter(function (g) {
        if (!g || !isId(String(g.id)) || seen.has(String(g.id))) return false;
        seen.add(String(g.id));
        return true;
      })
      .map(function (g) {
        return { id: String(g.id), h: String(g.h || ''), t: Number(g.t) || 0 };
      })
      .slice(-MAX);
  }

  function validOp(o) {
    return (
      o &&
      (o.op === 'add' || o.op === 'remove' || o.op === 'merge') &&
      Array.isArray(o.ids) &&
      o.ids.length > 0 &&
      o.ids.every(isId)
    );
  }

  function applyOps(base, ops) {
    var list = base.slice();
    ops.forEach(function (o) {
      if (o.op === 'remove') {
        var drop = toSet(o.ids);
        list = list.filter(function (id) {
          return !drop.has(id);
        });
      } else {
        var have = toSet(list);
        o.ids.forEach(function (id) {
          if (!have.has(id) && list.length < MAX) {
            list.push(id);
            have.add(id);
          }
        });
      }
    });
    return list;
  }

  function recompute() {
    ids = account
      ? applyOps(confirmed, queue)
      : guest.map(function (g) {
          return g.id;
        });
  }

  function has(id) {
    return ids.indexOf(String(id)) !== -1;
  }

  function saveQueue() {
    if (queue.length) store.set(KEYS.queue, { cid: token.cid, ops: queue });
    else store.del(KEYS.queue);
  }

  function saveAcct() {
    store.set(KEYS.acct, { cid: token.cid, ids: confirmed, at: Date.now() });
  }

  function rememberHandle(id, handle) {
    if (!id || !handle || handles[id] === handle) return;
    handles[id] = handle;
    var keys = Object.keys(handles);
    if (keys.length > 400) {
      var keep = toSet(ids);
      keys.forEach(function (k) {
        if (!keep.has(k)) delete handles[k];
      });
    }
    store.set(KEYS.handles, handles);
  }

  function handleFor(id) {
    if (handles[id]) return handles[id];
    for (var i = 0; i < guest.length; i++) if (guest[i].id === id && guest[i].h) return guest[i].h;
    var btn = document.querySelector('[data-aps-fav="' + id + '"][data-aps-fav-handle]');
    return btn ? btn.getAttribute('data-aps-fav-handle') : '';
  }

  function init() {
    if (account) {
      var serverIds = (Array.isArray(CFG.ids) ? CFG.ids : []).map(String).filter(isId);
      var cache = store.get(KEYS.acct, null);
      var useCache = !!(
        cache &&
        cache.cid === token.cid &&
        Array.isArray(cache.ids) &&
        Date.now() - cache.at < FRESH_MS
      );
      confirmed = useCache ? cache.ids.map(String).filter(isId) : serverIds;
      needsCheck = useCache && confirmed.join(',') !== serverIds.join(',');
      var saved = store.get(KEYS.queue, null);
      queue = saved && saved.cid === token.cid && Array.isArray(saved.ops) ? saved.ops.filter(validOp) : [];

      // Favorites saved on this device before signing in move into the account.
      var local = normalizeGuest(store.get(KEYS.guest, []));
      if (local.length) {
        local.forEach(function (g) {
          if (g.h) rememberHandle(g.id, g.h);
        });
        queue.push({
          op: 'merge',
          ids: local.map(function (g) {
            return g.id;
          }),
        });
        saveQueue();
        store.del(KEYS.guest);
      }
    } else {
      store.del(KEYS.acct); // don't leave a signed-out customer's list on a shared computer
      guest = normalizeGuest(store.get(KEYS.guest, []));
    }
    recompute();
  }

  // ------------------------------------------------------------------ talking to the Worker

  function refreshToken() {
    return fetch(ROOT + '?section_id=aps-fav-token&t=' + Date.now(), { credentials: 'same-origin' })
      .then(function (r) {
        return r.ok ? r.text() : '';
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var t = readJson(doc.querySelector('[data-aps-fav-token]'));
        if (validToken(t) && String(t.cid) === token.cid) {
          token = { cid: String(t.cid), ts: String(t.ts), sig: String(t.sig) };
          return true;
        }
        return false;
      })
      .catch(function () {
        return false;
      });
  }

  function send(op, refreshed) {
    var body = JSON.stringify({ cid: token.cid, ts: token.ts, sig: token.sig, op: op.op, ids: op.ids });
    return fetch(ENDPOINT + '/favorites', {
      method: 'POST',
      mode: 'cors',
      credentials: 'omit',
      headers: { 'Content-Type': 'text/plain;charset=UTF-8' }, // a "simple" request: no pre-flight
      body: body,
      keepalive: body.length < 60000, // lets the save finish if the shopper clicks away
    })
      .then(
        function (r) {
          return r
            .json()
            .catch(function () {
              return {};
            })
            .then(function (b) {
              b = b || {};
              b.status = r.status;
              return b;
            });
        },
        function () {
          return { status: 0, error: 'network' };
        }
      )
      .then(function (b) {
        if (b.status === 200 && b.ok && Array.isArray(b.ids)) {
          return {
            ok: true,
            ids: b.ids.map(String).filter(isId),
            added: b.added || [],
            skipped: b.skipped || [],
            invalid: b.invalid || [],
            full: !!b.full,
          };
        }
        if (!refreshed && (b.error === 'expired' || b.error === 'bad_token')) {
          // The page's pass is too old (or the secret was changed): get a fresh one and try once more.
          return refreshToken().then(function (ok) {
            return ok ? send(op, true) : { ok: false, retry: false, error: b.error };
          });
        }
        if (b.status === 0 || b.status === 409 || b.status === 429 || b.status >= 500) {
          return { ok: false, retry: true, error: b.error || 'http_' + b.status };
        }
        return { ok: false, retry: false, error: b.error || 'http_' + b.status };
      });
  }

  function scheduleRetry() {
    failures += 1;
    var delays = [2000, 5000, 15000, 30000, 60000];
    var wait = delays[Math.min(failures - 1, delays.length - 1)];
    if (failures >= 3 && !warnedRetry) {
      warnedRetry = true;
      toast(S.retrying);
    }
    retryTimer = setTimeout(function () {
      retryTimer = null;
      flush();
    }, wait);
  }

  function flush() {
    if (!account || inFlight || retryTimer || !queue.length) return;
    inFlight = true;
    var op = queue[0];
    send(op).then(function (res) {
      inFlight = false;
      if (res.ok) {
        failures = 0;
        changes += 1;
        queue.shift();
        saveQueue();
        confirmed = res.ids;
        saveAcct();
        recompute();
        render();
        if (op.op === 'merge') {
          var n = res.added.length;
          if (n) toast(fill(n === 1 ? S.merged_one : S.merged_other, { count: n }), page ? null : { href: PAGE_URL, text: S.view });
          if (res.skipped.length) setTimeout(function () {
            toast(fill(S.full, { max: MAX }));
          }, 4500);
        } else if (res.full) {
          toast(fill(S.full, { max: MAX }));
        }
      } else if (res.retry) {
        scheduleRetry();
      } else {
        queue.shift();
        if (op.op === 'merge') {
          // Couldn't move this device's favorites into the account: keep them on the device.
          var back = normalizeGuest(
            store.get(KEYS.guest, []).concat(
              op.ids.map(function (id) {
                return { id: id, h: handles[id] || '', t: Date.now() };
              })
            )
          );
          store.set(KEYS.guest, back);
        }
        saveQueue();
        recompute();
        render();
        toast(S.error);
      }
      flush();
    });
  }

  function enqueue(op) {
    // A change to the same product that hasn't been sent yet is replaced by this one.
    var start = inFlight ? 1 : 0;
    for (var i = queue.length - 1; i >= start; i--) {
      var o = queue[i];
      if (o.op !== 'merge' && o.ids.length === 1 && o.ids[0] === op.ids[0]) queue.splice(i, 1);
    }
    changes += 1;
    queue.push(op);
    saveQueue();
    flush();
  }

  // Asks the Worker for the saved list (used when this device's copy and the page disagree,
  // e.g. favorites were changed on another phone or computer a moment ago).
  function checkWithWorker() {
    if (!account || queue.length || inFlight) return;
    var at = changes;
    send({ op: 'get', ids: [] }).then(function (res) {
      if (res.ok && changes === at && !queue.length && !inFlight) {
        confirmed = res.ids;
        saveAcct();
        recompute();
        render();
      }
    });
  }

  // ------------------------------------------------------------------ hearts

  function paintBtn(btn, on) {
    var pressed = on ? 'true' : 'false';
    if (btn.getAttribute('aria-pressed') !== pressed) btn.setAttribute('aria-pressed', pressed);
    var name = btn.getAttribute('data-aps-fav-name');
    var label = (on ? S.remove : S.add) + (name ? ': ' + name : '');
    if (btn.getAttribute('aria-label') !== label) btn.setAttribute('aria-label', label);
  }

  function paintCount(el) {
    var n = ids.length;
    var hide = n === 0;
    if (el.hidden !== hide) el.hidden = hide;
    var num = el.querySelector('[data-aps-fav-count-num]');
    var text = n < 100 ? String(n) : '99+';
    if (num && num.textContent !== text) num.textContent = text;
    var label = el.querySelector('[data-aps-fav-count-text]');
    var words = fill(n === 1 ? S.count_one : S.count_other, { count: n });
    if (label && label.textContent !== words) label.textContent = words;
  }

  function render() {
    var set = toSet(ids);
    var buttons = document.querySelectorAll('[data-aps-fav]');
    for (var i = 0; i < buttons.length; i++) paintBtn(buttons[i], set.has(buttons[i].getAttribute('data-aps-fav')));
    var counts = document.querySelectorAll('[data-aps-fav-count]');
    for (var j = 0; j < counts.length; j++) paintCount(counts[j]);
    var headers = document.querySelectorAll('[data-aps-fav-header]');
    for (var k = 0; k < headers.length; k++) headers[k].classList.toggle('is-filled', ids.length > 0);
    if (page) page.sync();
  }

  function pop(btn) {
    if (!btn) return;
    btn.classList.remove('is-popping');
    void btn.offsetWidth; // restart the animation
    btn.classList.add('is-popping');
    setTimeout(function () {
      btn.classList.remove('is-popping');
    }, 400);
  }

  function firstSaveMessage() {
    if (page || session.get(KEYS.noted, false)) return;
    session.set(KEYS.noted, true);
    if (account || CFG.signedIn) toast(S.saved, { href: PAGE_URL, text: S.view });
    else toast(S.saved_device, CFG.login ? { href: CFG.login, text: S.sign_in } : null);
  }

  function toggle(id, handle, btn) {
    id = String(id);
    if (!isId(id)) return;
    var on = !has(id);
    if (on && ids.length >= MAX) {
      toast(fill(S.full, { max: MAX }));
      return;
    }
    if (handle) rememberHandle(id, handle);
    if (account) {
      enqueue({ op: on ? 'add' : 'remove', ids: [id] });
    } else {
      guest = on
        ? guest.concat([{ id: id, h: handle || '', t: Date.now() }])
        : guest.filter(function (g) {
            return g.id !== id;
          });
      store.set(KEYS.guest, guest);
    }
    recompute();
    render();
    if (on) {
      pop(btn);
      firstSaveMessage();
    }
  }

  document.addEventListener('click', function (event) {
    var btn = event.target && event.target.closest ? event.target.closest('[data-aps-fav]') : null;
    if (!btn) return;
    event.preventDefault();
    toggle(btn.getAttribute('data-aps-fav'), btn.getAttribute('data-aps-fav-handle'), btn);
  });

  // ------------------------------------------------------------------ Selleasy upsell cards

  // Selleasy builds its product cards in the browser and doesn't put the product id on them,
  // so we look the product up by its exact title (Shopify's search suggestions) once per visit.
  var titleCache = session.get(KEYS.titles, {}) || {};
  var lookups = {};

  function lookupProduct(title) {
    var key = norm(title);
    if (Object.prototype.hasOwnProperty.call(titleCache, key)) return Promise.resolve(titleCache[key] || null);
    if (lookups[key]) return lookups[key];
    var params = new URLSearchParams();
    params.set('q', title);
    params.set('resources[type]', 'product');
    params.set('resources[limit]', '10');
    params.set('resources[options][unavailable_products]', 'last');
    params.set('resources[options][fields]', 'title');
    lookups[key] = fetch(ROOT + 'search/suggest.json?' + params.toString(), {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
      .then(function (r) {
        return r.ok ? r.json() : null;
      })
      .then(function (data) {
        var list = (data && data.resources && data.resources.results && data.resources.results.products) || [];
        var hits = list.filter(function (p) {
          return norm(p.title) === key;
        });
        var found = hits.length === 1 ? { id: String(hits[0].id), h: hits[0].handle, n: hits[0].title } : null;
        titleCache[key] = found || 0;
        session.set(KEYS.titles, titleCache);
        return found;
      })
      .catch(function () {
        return null;
      })
      .then(function (found) {
        delete lookups[key];
        return found;
      });
    return lookups[key];
  }

  function makeHeart(p, ctx) {
    var b = document.createElement('button');
    b.type = 'button';
    b.className = 'aps-fav aps-fav--' + ctx;
    b.setAttribute('data-aps-fav', p.id);
    b.setAttribute('data-aps-fav-handle', p.h || '');
    b.setAttribute('data-aps-fav-name', p.n || '');
    b.innerHTML = HEART_SVG;
    paintBtn(b, has(p.id));
    return b;
  }

  function placeUpsellHeart(btn) {
    var box = btn.parentElement;
    var img = box && box.querySelector('img.ppa-prod-img');
    if (!img) return;
    var size = btn.offsetWidth || 24;
    var left = img.offsetLeft + img.offsetWidth - size - 4;
    btn.style.left = Math.max(0, left) + 'px';
    btn.style.top = img.offsetTop + 4 + 'px';
  }

  function scanUpsells() {
    var imgs = document.querySelectorAll(UPSELL_IMG);
    Array.prototype.forEach.call(imgs, function (img) {
      var box = img.closest(UPSELL_BOX) || img.parentElement;
      if (!box || box.querySelector(':scope > .aps-fav--upsell')) return;
      var titleEl = box.querySelector('[class*="ppa-prod-details-title"]');
      var title = (titleEl ? titleEl.textContent : img.getAttribute('alt') || '').trim();
      if (!title) return;
      lookupProduct(title).then(function (p) {
        if (!p || !box.isConnected || box.querySelector(':scope > .aps-fav--upsell')) return;
        box.classList.add('aps-fav-host');
        var btn = makeHeart(p, 'upsell');
        box.appendChild(btn);
        placeUpsellHeart(btn);
        if (!img.complete) img.addEventListener('load', function () { placeUpsellHeart(btn); }, { once: true });
      });
    });
  }

  var resizeTimer;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      Array.prototype.forEach.call(document.querySelectorAll('.aps-fav--upsell'), placeUpsellHeart);
    }, 150);
  });

  // ------------------------------------------------------------------ small message

  var toastEl = null;
  var toastTimer = null;

  function hideToast() {
    if (toastEl) toastEl.classList.remove('is-visible');
  }

  function toast(message, link) {
    if (!document.body) return;
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'aps-fav-toast';
      toastEl.setAttribute('role', 'status');
      toastEl.setAttribute('aria-live', 'polite');
      document.body.appendChild(toastEl);
    }
    toastEl.textContent = '';
    var span = document.createElement('span');
    span.textContent = message;
    toastEl.appendChild(span);
    if (link && link.href) {
      var a = document.createElement('a');
      a.href = link.href;
      a.textContent = link.text;
      toastEl.appendChild(a);
    }
    var close = document.createElement('button');
    close.type = 'button';
    close.setAttribute('aria-label', 'Close');
    close.textContent = '×';
    close.addEventListener('click', hideToast);
    toastEl.appendChild(close);
    requestAnimationFrame(function () {
      toastEl.classList.add('is-visible');
    });
    clearTimeout(toastTimer);
    toastTimer = setTimeout(hideToast, link ? 6500 : 4500);
  }

  // ------------------------------------------------------------------ My Favorites page

  function FavPage(root) {
    var self = this;
    this.root = root;
    this.grid = root.querySelector('[data-grid]');
    this.live = root.getAttribute('data-preview') === 'live';
    this.signedIn = root.getAttribute('data-signed-in') === 'true';
    // Hide cards that are no longer favorites only when this page and the hearts use the same list.
    // (If the Worker isn't set up yet, a signed-in shopper's hearts use the device list instead.)
    this.reconcile = account || !this.signedIn;
    this.text = readJson(root.querySelector('[data-aps-fav-page-text]')) || {};
    this.pageSize = parseInt(root.getAttribute('data-page-size'), 10) || 24;
    this.toolbarMin = parseInt(root.getAttribute('data-toolbar-min'), 10) || 6;
    this.state = { query: '', group: 'all', sort: 'recent', limit: this.pageSize };
    this.loading = {};
    this.failed = {};
    this.started = false;
    this.pendingLoads = 0;
    this.toolbarShown = false;
    this.chipSig = '';
    this.toolbar = root.querySelector('[data-toolbar]');
    this.chips = root.querySelector('[data-chips]');
    this.search = root.querySelector('[data-search]');
    this.sort = root.querySelector('[data-sort]');

    var timer;
    if (this.search) {
      this.search.addEventListener('input', function () {
        clearTimeout(timer);
        timer = setTimeout(function () {
          self.state.query = self.search.value;
          self.state.limit = self.pageSize;
          self.apply();
        }, 150);
      });
    }
    if (this.sort) {
      this.sort.addEventListener('change', function () {
        self.state.sort = self.sort.value;
        self.apply();
      });
    }
    if (this.chips) {
      this.chips.addEventListener('click', function (e) {
        var chip = e.target.closest('[data-group-filter]');
        if (!chip) return;
        self.state.group = chip.getAttribute('data-group-filter');
        self.state.limit = self.pageSize;
        self.chipSig = '';
        self.apply();
      });
    }
    var more = root.querySelector('[data-show-more]');
    if (more) {
      more.addEventListener('click', function () {
        self.state.limit += self.pageSize;
        self.apply();
      });
    }
    var clear = root.querySelector('[data-clear]');
    if (clear) {
      clear.addEventListener('click', function () {
        if (self.search) self.search.value = '';
        self.state.query = '';
        self.state.group = 'all';
        self.state.limit = self.pageSize;
        self.chipSig = '';
        self.apply();
        if (self.search) self.search.focus();
      });
    }
    root.addEventListener('change', function (e) {
      var select = e.target.closest('[data-aps-fav-variant]');
      if (!select) return;
      var option = select.options[select.selectedIndex];
      var card = select.closest('[data-aps-fav-item]');
      if (!option || !card) return;
      var price = card.querySelector('[data-aps-fav-price]');
      var compare = card.querySelector('[data-aps-fav-compare]');
      if (price && option.getAttribute('data-price')) price.textContent = option.getAttribute('data-price');
      if (compare) {
        var c = option.getAttribute('data-compare') || '';
        compare.textContent = c;
        compare.hidden = !c;
      }
    });
  }

  FavPage.prototype.items = function () {
    return Array.prototype.slice.call(this.grid.querySelectorAll('[data-aps-fav-item]'));
  };

  FavPage.prototype.sync = function () {
    var self = this;
    if (!this.live) {
      this.apply();
      return;
    }
    var position = {};
    ids.forEach(function (id, i) {
      position[id] = i + 1;
    });
    var rendered = {};
    this.items().forEach(function (li) {
      var id = li.getAttribute('data-aps-fav-item');
      rendered[id] = true;
      if (position[id]) {
        li.removeAttribute('data-gone');
        li.classList.remove('is-removed');
        // Cards keep their place once shown (so an undo doesn't make a card jump); new cards go on top.
        if (!self.started || !parseFloat(li.getAttribute('data-order'))) {
          li.setAttribute('data-order', String(position[id] + (self.started ? 1000 : 0)));
        }
      } else if (!self.reconcile) {
        return; // leave the card as the page drew it
      } else if (!self.started) {
        li.setAttribute('data-gone', ''); // removed before this page loaded (e.g. on another page a moment ago)
      } else if (!li.hasAttribute('data-gone')) {
        li.classList.add('is-removed'); // removed here: keep it dimmed so they can tap the heart again
      }
    });
    this.started = true;
    var missing = ids.filter(function (id) {
      return !rendered[id] && !self.loading[id] && !self.failed[id];
    });
    if (missing.length) this.load(missing);
    this.apply();
  };

  FavPage.prototype.load = function (list) {
    var self = this;
    var jobs = [];
    list.forEach(function (id) {
      var handle = handleFor(id);
      if (handle) jobs.push({ id: id, handle: handle });
      else self.failed[id] = true;
    });
    if (!jobs.length) return;
    jobs.forEach(function (j) {
      self.loading[j.id] = true;
    });
    this.pendingLoads += jobs.length;
    var next = 0;
    function worker() {
      if (next >= jobs.length) return Promise.resolve();
      var job = jobs[next++];
      return fetch(ROOT + 'products/' + encodeURIComponent(job.handle) + '?section_id=aps-fav-product-card', {
        credentials: 'same-origin',
      })
        .then(function (r) {
          if (r.status === 404) return { gone: true };
          return r.ok ? r.text() : { error: true };
        })
        .then(function (res) {
          if (typeof res !== 'string') {
            self.failed[job.id] = true;
            if (res.gone && !account) {
              guest = guest.filter(function (g) {
                return g.id !== job.id;
              });
              store.set(KEYS.guest, guest);
              recompute();
            }
            return;
          }
          var doc = new DOMParser().parseFromString(res, 'text/html');
          var li = doc.querySelector('[data-aps-fav-item]');
          if (!li || li.getAttribute('data-aps-fav-item') !== job.id) {
            self.failed[job.id] = true;
            return;
          }
          self.grid.appendChild(document.importNode(li, true));
        })
        .catch(function () {
          self.failed[job.id] = true;
        })
        .then(function () {
          delete self.loading[job.id];
          self.pendingLoads -= 1;
          return worker();
        });
    }
    this.apply();
    Promise.all([worker(), worker(), worker(), worker()]).then(function () {
      render();
    });
  };

  FavPage.prototype.buildChips = function (all) {
    var self = this;
    if (!this.chips) return;
    var groups = {};
    all.forEach(function (li) {
      var name = li.getAttribute('data-group') || 'Other';
      if (!groups[name]) groups[name] = { name: name, rank: parseInt(li.getAttribute('data-rank'), 10) || 50, n: 0 };
      if (!li.classList.contains('is-removed')) groups[name].n += 1;
    });
    var list = Object.keys(groups)
      .map(function (k) {
        return groups[k];
      })
      .sort(function (a, b) {
        return a.rank - b.rank || a.name.localeCompare(b.name);
      });
    if (this.state.group !== 'all' && !groups[this.state.group]) this.state.group = 'all';
    var sig =
      list
        .map(function (g) {
          return g.name + ':' + g.n;
        })
        .join('|') +
      '#' +
      this.state.group;
    if (sig === this.chipSig) return;
    this.chipSig = sig;
    this.chips.textContent = '';
    if (list.length < 2) {
      this.chips.hidden = true;
      return;
    }
    this.chips.hidden = false;
    function chip(value, label, count) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'aps-favs__chip';
      b.setAttribute('data-group-filter', value);
      b.setAttribute('aria-pressed', self.state.group === value ? 'true' : 'false');
      b.appendChild(document.createTextNode(label));
      if (count !== null) {
        var c = document.createElement('span');
        c.className = 'aps-favs__chip-count';
        c.textContent = String(count);
        b.appendChild(c);
      }
      self.chips.appendChild(b);
    }
    chip('all', this.text.all || 'All', null);
    list.forEach(function (g) {
      chip(g.name, g.name, g.n);
    });
  };

  FavPage.prototype.apply = function () {
    var self = this;
    var s = this.state;
    var root = this.root;
    var all = this.items().filter(function (li) {
      if (li.hasAttribute('data-gone')) {
        if (!li.hidden) li.hidden = true;
        return false;
      }
      return true;
    });
    var active = all.filter(function (li) {
      return !li.classList.contains('is-removed');
    });

    this.buildChips(all);

    function num(li, attr) {
      return parseFloat(li.getAttribute(attr)) || 0;
    }
    function byName(a, b) {
      return (a.getAttribute('data-name') || '').localeCompare(b.getAttribute('data-name') || '', undefined, {
        numeric: true,
        sensitivity: 'base',
      });
    }
    var sorters = {
      recent: function (a, b) {
        return num(b, 'data-order') - num(a, 'data-order') || byName(a, b);
      },
      az: byName,
      price: function (a, b) {
        return num(a, 'data-price') - num(b, 'data-price') || byName(a, b);
      },
    };
    var sorted = all.slice().sort(sorters[s.sort] || sorters.recent);
    // Only move cards when the order really changed (moving them makes the page do extra work).
    var current = this.items().filter(function (li) {
      return !li.hasAttribute('data-gone');
    });
    var same = current.length === sorted.length && current.every(function (li, i) {
      return li === sorted[i];
    });
    if (!same) {
      sorted.forEach(function (li) {
        self.grid.appendChild(li);
      });
    }

    var query = norm(s.query);
    var matches = sorted.filter(function (li) {
      var groupOk = s.group === 'all' || li.getAttribute('data-group') === s.group;
      return groupOk && (!query || (li.getAttribute('data-name') || '').indexOf(query) !== -1);
    });
    var visible = toSet(matches.slice(0, s.limit));
    sorted.forEach(function (li) {
      var hide = !visible.has(li);
      if (li.hidden !== hide) li.hidden = hide;
    });

    var total = active.length;
    var filtering = query !== '' || s.group !== 'all';
    var shownActive = matches.filter(function (li) {
      return !li.classList.contains('is-removed');
    }).length;
    var label = '';
    if (total > 0) {
      label = filtering
        ? fill(this.text.filtered || '[shown] of [total]', { shown: shownActive, total: total })
        : fill(total === 1 ? this.text.one || '1 favorite' : this.text.other || '[count] favorites', { count: total });
    }
    var countEl = root.querySelector('[data-count]');
    if (countEl && countEl.textContent !== label) countEl.textContent = label;

    if (all.length >= this.toolbarMin) this.toolbarShown = true;
    if (this.toolbar) this.toolbar.hidden = !this.toolbarShown;

    var noResults = root.querySelector('[data-no-results]');
    if (noResults) {
      noResults.hidden = !(all.length > 0 && matches.length === 0);
      var nr = noResults.querySelector('[data-no-results-text]');
      if (nr && matches.length === 0) nr.textContent = fill(this.text.noResults || 'No matches', { query: s.query.trim() });
    }

    var more = root.querySelector('[data-more]');
    if (more) {
      var shown = Math.min(s.limit, matches.length);
      more.hidden = matches.length <= s.limit;
      var pt = more.querySelector('[data-progress-text]');
      if (pt) pt.textContent = fill(this.text.showing || '[shown] / [total]', { shown: shown, total: matches.length });
      var bar = more.querySelector('[data-progress-bar]');
      if (bar) bar.style.width = (matches.length ? (shown / matches.length) * 100 : 0) + '%';
    }

    var loadingEl = root.querySelector('[data-loading]');
    var isLoading = this.pendingLoads > 0;
    if (loadingEl) loadingEl.hidden = !(isLoading && all.length === 0);

    var empty = root.querySelector('[data-empty]');
    if (empty) {
      empty.hidden = !(all.length === 0 && !isLoading && (this.signedIn || this.started || !this.live));
    }
  };

  // ------------------------------------------------------------------ watch for new cards

  // Runs right after the page changes (before the browser paints), so new cards never flash an empty heart.
  var scheduled = false;
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    Promise.resolve().then(function () {
      scheduled = false;
      render();
      scanUpsells();
    });
  }

  function interesting(node) {
    if (!node || node.nodeType !== 1) return false;
    var sel = '[data-aps-fav], [data-aps-fav-count], img.ppa-prod-img';
    if (node.matches(sel)) return true;
    return !!node.querySelector(sel);
  }

  function start() {
    init();
    var root = document.querySelector('[data-aps-fav-page]');
    if (root) page = new FavPage(root);
    render();
    scanUpsells();
    flush();
    if (needsCheck) checkWithWorker();

    if (window.MutationObserver && document.body) {
      new MutationObserver(function (records) {
        for (var i = 0; i < records.length; i++) {
          var added = records[i].addedNodes;
          for (var j = 0; j < added.length; j++) {
            if (interesting(added[j])) {
              schedule();
              return;
            }
          }
        }
      }).observe(document.body, { childList: true, subtree: true });
    }

    window.addEventListener('online', flush);

    // Theme editor: changing a My Favorites setting redraws the section, so start the page script again.
    document.addEventListener('shopify:section:unload', function (e) {
      if (page && e.target && e.target.contains && e.target.contains(page.root)) page = null;
    });
    document.addEventListener('shopify:section:load', function (e) {
      var r = e.target && e.target.querySelector ? e.target.querySelector('[data-aps-fav-page]') : null;
      if (r) {
        page = new FavPage(r);
        render();
      }
    });
    window.addEventListener('storage', function (e) {
      if (!account && e.key === KEYS.guest) {
        guest = normalizeGuest(store.get(KEYS.guest, []));
        recompute();
        render();
      } else if (account && e.key === KEYS.acct) {
        var c = store.get(KEYS.acct, null);
        if (c && c.cid === token.cid && Array.isArray(c.ids)) {
          changes += 1;
          confirmed = c.ids.map(String).filter(isId);
          recompute();
          render();
        }
      }
    });
  }

  window.ApsFavorites = {
    version: '1.0.1',
    ids: function () {
      return ids.slice();
    },
    has: has,
    toggle: function (id, handle) {
      toggle(id, handle, null);
    },
    refresh: schedule,
    mode: function () {
      return account ? 'account' : 'device';
    },
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
